import { useCallback, useRef } from 'react';
import type { Order, OrderStatus } from '../../types/Order';
import { PaymentChange, putPayment, putStatus, saveOrder } from './orderApi';
import type { OrderOps } from './useOrdersFeed';
import { NEXT_STATUS } from '../../components/ui/status';

/**
 * OrderOps for a list or page that owns its orders in local state (History, Detail).
 * `patch` applies a change to one order; on failure the previous order is patched back and the error rethrown.
 */
export function useOrderEditor(orders: readonly Order[], patch: (id: string, change: Partial<Order>) => void): OrderOps {
    const latest = useRef(orders);
    latest.current = orders;

    const mutate = useCallback(
        async (id: string, change: Partial<Order>, send: () => Promise<Order>) => {
            const before = latest.current.find((o) => o.id === id);
            if (!before) return;
            patch(id, change);
            try {
                await saveOrder(before, change, send);
            } catch (e) {
                patch(id, before);
                throw e;
            }
        },
        [patch]
    );

    const revert = useCallback((id: string, to: OrderStatus) => mutate(id, { status: to }, () => putStatus(id, to)), [mutate]);
    return {
        revert,
        cancel: (id) => revert(id, 'cancelled'),
        advance: async (id) => {
            const order = latest.current.find((o) => o.id === id);
            const next = order && NEXT_STATUS[order.status];
            if (next) await revert(id, next);
        },
        setPayment: (id: string, p: PaymentChange) =>
            mutate(id, { paymentStatus: p.status, ...(p.method ? { paymentMethod: p.method } : {}) }, () => putPayment(id, p)),
    };
}
